import { useEffect, useState } from 'react';
import { IProduct, IProductSelectorTree } from '@topshots/core-models';
import { Button, ButtonTier } from 'shared-components';
import { sortOptions } from './selector-editor-utils';
import styles from './product-selector-editor.module.css';

export const SelectorPreview = ({
  tree,
  productsById,
}: {
  tree: IProductSelectorTree;
  productsById: Map<string, IProduct>;
}) => {
  const [currentNodeId, setCurrentNodeId] = useState(tree.rootNodeId);
  const [history, setHistory] = useState<string[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );

  useEffect(() => {
    setCurrentNodeId(tree.rootNodeId);
    setHistory([]);
    setSelectedProductId(null);
  }, [tree.rootNodeId, tree.nodes]);

  const currentNode = tree.nodes[currentNodeId];
  const selectedProduct = selectedProductId
    ? productsById.get(selectedProductId)
    : undefined;

  if (!currentNode) {
    return (
      <div className={styles.previewEmpty}>Starting question not found.</div>
    );
  }

  return (
    <div className={styles.previewPanel}>
      {selectedProductId ? (
        <>
          <div className={styles.previewTitle}>Product Confirmation</div>
          <div className={styles.previewProductName}>
            {selectedProduct?.name ?? selectedProductId}
          </div>
          <div className={styles.previewActionsRow}>
            <Button
              tier={ButtonTier.Secondary}
              onClick={() => {
                setSelectedProductId(null);
              }}
            >
              Back
            </Button>
            <Button
              tier={ButtonTier.Tertiary}
              onClick={() => {
                setHistory([]);
                setSelectedProductId(null);
                setCurrentNodeId(tree.rootNodeId);
              }}
            >
              Start Over
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className={styles.previewTitle}>{currentNode.prompt}</div>
          <div className={styles.previewOptions}>
            {sortOptions(currentNode.options).map((option) => (
              <Button
                key={option.id}
                tier={ButtonTier.Secondary}
                className={styles.previewOptionButton}
                onClick={() => {
                  if (option.next.type === 'node') {
                    setHistory((prev) => [...prev, currentNodeId]);
                    setCurrentNodeId(option.next.nodeId);
                  } else if (option.next.type === 'product') {
                    setSelectedProductId(option.next.productId);
                  }
                }}
              >
                {option.label || 'Untitled Option'}
              </Button>
            ))}
          </div>
          <div className={styles.previewActionsRow}>
            <Button
              tier={ButtonTier.Tertiary}
              onClick={() => {
                const previous = history[history.length - 1];
                if (!previous) return;
                setHistory((prev) => prev.slice(0, -1));
                setCurrentNodeId(previous);
              }}
              disabled={history.length === 0}
            >
              Back
            </Button>
            <Button
              tier={ButtonTier.Tertiary}
              onClick={() => {
                setHistory([]);
                setCurrentNodeId(tree.rootNodeId);
              }}
            >
              Start Over
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
